import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { LISTING_TYPES, PROPERTY_STATUSES, PROPERTY_TYPES, STATUS_BADGE, SUBTYPES } from "@/lib/constants";
import { formatINR, sizeOf } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/PlaceholderPage";

const ALL = "__all__";

const FilterSelect = ({ value, onChange, placeholder, options, testId, className }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={`rounded-lg bg-white ${className || "w-[160px]"}`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((o) => (
        <SelectItem key={o} value={o}>{o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function PropertiesPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    property_type: searchParams.get("property_type") || ALL,
    property_subtype: ALL,
    listing_type: ALL,
    status: searchParams.get("status") || ALL,
    locality: "", price_min: "", price_max: "", sort: "newest",
  });

  const set = (key) => (value) =>
    setFilters((f) => ({ ...f, [key]: value, ...(key === "property_type" ? { property_subtype: ALL } : {}) }));

  const params = Object.fromEntries(
    Object.entries({
      property_type: filters.property_type === ALL ? "" : filters.property_type,
      property_subtype: filters.property_subtype === ALL ? "" : filters.property_subtype,
      listing_type: filters.listing_type === ALL ? "" : filters.listing_type,
      status: filters.status === ALL ? "" : filters.status,
      locality: filters.locality.trim(),
      price_min: filters.price_min,
      price_max: filters.price_max,
      sort: filters.sort,
    }).filter(([, v]) => v !== "")
  );

  const { data: properties = [], isLoading } = useQuery({
    queryKey: ["properties", params],
    queryFn: async () => (await api.get("/properties", { params })).data,
  });

  return (
    <div className="p-6 sm:p-8" data-testid="properties-page">
      <PageHeader title="Properties" subtitle="Supply side — owner listings for sale & rent">
        {currentUser?.role === "admin" && (
          <Button
            onClick={() => navigate("/properties/new")}
            data-testid="add-property-button"
            className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Property
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <FilterSelect value={filters.property_type} onChange={set("property_type")} placeholder="All Types" options={PROPERTY_TYPES} testId="filter-property-type" />
        <FilterSelect value={filters.property_subtype} onChange={set("property_subtype")} placeholder="All Subtypes" options={filters.property_type === ALL ? [] : SUBTYPES[filters.property_type]} testId="filter-property-subtype" className="w-[180px]" />
        <FilterSelect value={filters.listing_type} onChange={set("listing_type")} placeholder="Sale & Rent" options={LISTING_TYPES} testId="filter-listing-type" className="w-[140px]" />
        <FilterSelect value={filters.status} onChange={set("status")} placeholder="All Statuses" options={PROPERTY_STATUSES} testId="filter-status" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={filters.locality}
            onChange={(e) => set("locality")(e.target.value)}
            placeholder="Search locality…"
            data-testid="filter-locality-search"
            className="pl-9 w-[200px] rounded-lg bg-white"
          />
        </div>
        <Input value={filters.price_min} onChange={(e) => set("price_min")(e.target.value)} type="number" placeholder="Min ₹" data-testid="filter-price-min" className="w-[110px] rounded-lg bg-white" />
        <Input value={filters.price_max} onChange={(e) => set("price_max")(e.target.value)} type="number" placeholder="Max ₹" data-testid="filter-price-max" className="w-[110px] rounded-lg bg-white" />
        <Select value={filters.sort} onValueChange={set("sort")}>
          <SelectTrigger data-testid="sort-select" className="w-[170px] rounded-lg bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="price_asc">Price: low → high</SelectItem>
            <SelectItem value="price_desc">Price: high → low</SelectItem>
            <SelectItem value="size_asc">Size: small → large</SelectItem>
            <SelectItem value="size_desc">Size: large → small</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] overflow-x-auto animate-fade-up">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/60">
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Listing</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Price / Rent</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
            ) : properties.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12" data-testid="properties-empty">
                  <p className="text-slate-400">No properties yet for these filters.</p>
                  {currentUser?.role === "admin" && (
                    <Button onClick={() => navigate("/properties/new")} variant="outline" data-testid="empty-add-property" className="mt-3 rounded-lg border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                      <Plus className="h-4 w-4 mr-2" /> Add your first property
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              properties.map((p) => (
                <TableRow
                  key={p.id}
                  onClick={() => navigate(`/properties/${p.id}`)}
                  data-testid={`property-row-${p.id}`}
                  className="cursor-pointer hover:bg-emerald-50/40"
                >
                  <TableCell className="font-medium text-emerald-950 max-w-[240px] truncate">{p.title}</TableCell>
                  <TableCell>
                    <span className="text-slate-700">{p.property_type}</span>
                    {p.property_subtype && <span className="text-slate-400 text-xs block">{p.property_subtype}</span>}
                  </TableCell>
                  <TableCell>{p.listing_type}</TableCell>
                  <TableCell className="text-slate-600">{p.locality}, {p.city}</TableCell>
                  <TableCell className="text-slate-600">{sizeOf(p)}</TableCell>
                  <TableCell className="font-medium text-emerald-900">
                    {formatINR(p.price_or_rent_amount)}{p.listing_type === "Rent" ? "/mo" : ""}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_BADGE[p.status]}>{p.status}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
